/**
 * ComplianceTab - TASK-229
 * Main container component with sub-navigation for compliance sections
 *
 * Replaces separate KYC and Risk Profile tabs with a unified compliance view.
 * Wraps content in ComplianceProvider for shared state management.
 */

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { ComplianceProvider, useComplianceContext } from '../../contexts/ComplianceContext';
import { useComplianceLoadingState } from '../../hooks/useCompliance';
import { ComplianceHeader } from './ComplianceHeader';
import { ComplianceSubNav, type ComplianceSubTab } from './ComplianceSubNav';
import { FormsSubTab } from './FormsSubTab';
import { OverviewSubTab } from './overview/OverviewSubTab';
import { ScreeningSubTab } from './screening/ScreeningSubTab';
import { TransactionsSubTab } from './transactions/TransactionsSubTab';
import { RiskRatingSubTab } from './risk-rating/RiskRatingSubTab';
import { Loader2 } from 'lucide-react';
// TASK-232: Import prefill store
import { useExtractionPrefillStore } from '../../stores/extractionPrefillStore';
// TASK-374: CO Review Queue (lazy import to avoid loading for non-CO users)
const COReviewQueue = lazy(() => import('./review/COReviewQueue'));

// Valid sub-tabs for URL validation
const VALID_SUBTABS: ComplianceSubTab[] = [
  'overview',
  'review', // TASK-374: CO Review sub-tab
  'forms',
  'screening',
  'transactions',
  'risk-rating',
];

interface ComplianceTabProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  initialSubTab?: ComplianceSubTab;
  onNavigateToDocuments?: () => void;
}

/**
 * Inner content component that can use ComplianceContext hooks
 */
const ComplianceTabContent: React.FC<{
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  activeSubTab: ComplianceSubTab;
  onSubTabChange: (tab: ComplianceSubTab) => void;
  onNavigateToDocuments?: () => void;
}> = ({
  clientId,
  clientName,
  clientEmail,
  clientPhone,
  activeSubTab,
  onSubTabChange,
  onNavigateToDocuments,
}) => {
  const { loading, error } = useComplianceLoadingState();
  const { ingestFromExtraction, ingestFromRawPreviewData } = useComplianceContext();

  // TASK-232: Prefill store integration
  const pendingExtraction = useExtractionPrefillStore((state) => state.pendingExtraction);
  const clearPendingExtraction = useExtractionPrefillStore((state) => state.clearPendingExtraction);
  const hasProcessedRef = useRef(false);

  // TASK-232: Auto-ingest pending extraction when component mounts
  useEffect(() => {
    // Only process if:
    // 1. We have pending extraction
    // 2. It's for this client
    // 3. We haven't processed it yet
    // 4. Loading is complete
    if (
      pendingExtraction &&
      pendingExtraction.clientId === clientId &&
      !hasProcessedRef.current &&
      !loading
    ) {
      logger.debug('🚀 TASK-232: ComplianceTab detected pending extraction, auto-ingesting...');
      hasProcessedRef.current = true;

      try {
        // TASK-232: Prefer raw data (complete) over transformed extractionData (loses data)
        if (pendingExtraction.rawKycData || pendingExtraction.rawRiskData) {
          logger.debug('📋 TASK-232: Using raw preview data (bypasses lossy transformation)');
          ingestFromRawPreviewData(
            pendingExtraction.rawKycData,
            pendingExtraction.rawRiskData,
            pendingExtraction.sourceName
          );
        } else {
          // Fallback to transformed extraction data
          logger.debug('📋 TASK-232: Using transformed extraction data (fallback)');
          ingestFromExtraction(pendingExtraction.extractionData);
        }

        // Show success toast
        toast.success(`Forms populated from "${pendingExtraction.sourceName}"`, {
          description: 'Review the data below and make any necessary edits.',
          duration: 5000,
        });

        // Clear the pending extraction
        clearPendingExtraction();

        // Navigate to forms subtab to show the populated data
        if (activeSubTab !== 'forms') {
          onSubTabChange('forms');
        }

        logger.debug('✅ TASK-232: Auto-ingestion complete');
      } catch (err) {
        logger.error('❌ TASK-232: Failed to ingest extraction:', err);
        toast.error('Failed to populate forms', {
          description: 'Please try again or enter data manually.',
        });
        clearPendingExtraction();
      }
    }

    // Reset the ref when clientId changes
    return () => {
      hasProcessedRef.current = false;
    };
  }, [pendingExtraction, clientId, loading, ingestFromExtraction, ingestFromRawPreviewData, clearPendingExtraction, activeSubTab, onSubTabChange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-500">Loading compliance data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-2">Failed to load compliance data</p>
        <p className="text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with risk badge */}
      <ComplianceHeader clientName={clientName} />

      {/* Sub-navigation */}
      <ComplianceSubNav activeTab={activeSubTab} onTabChange={onSubTabChange} />

      {/* Content area */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Overview Sub-Tab */}
        {activeSubTab === 'overview' && (
          <OverviewSubTab clientId={clientId} onNavigateToSubTab={onSubTabChange} />
        )}

        {/* TASK-374: Review Queue Sub-Tab (CO only) */}
        {activeSubTab === 'review' && (
          <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}>
            <COReviewQueue />
          </Suspense>
        )}

        {/* Forms Sub-Tab - Status cards with drill-down to full forms */}
        {activeSubTab === 'forms' && (
          <FormsSubTab
            clientId={clientId}
            clientName={clientName}
            clientEmail={clientEmail}
            clientPhone={clientPhone}
            onNavigateToDocuments={onNavigateToDocuments}
          />
        )}

        {/* Screening Sub-Tab */}
        {activeSubTab === 'screening' && (
          <ScreeningSubTab clientId={clientId} clientName={clientName} />
        )}

        {/* Transactions Sub-Tab */}
        {activeSubTab === 'transactions' && <TransactionsSubTab />}

        {/* Risk Rating Sub-Tab */}
        {activeSubTab === 'risk-rating' && <RiskRatingSubTab clientId={clientId} />}
      </div>
    </div>
  );
};

/**
 * Main ComplianceTab component
 * Wraps content in ComplianceProvider and manages URL-based sub-tab state
 */
export const ComplianceTab: React.FC<ComplianceTabProps> = ({
  clientId,
  clientName,
  clientEmail,
  clientPhone,
  initialSubTab = 'forms',
  onNavigateToDocuments,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSubTab, setActiveSubTab] = useState<ComplianceSubTab>(initialSubTab);

  // Sync sub-tab with URL on mount and when searchParams change
  useEffect(() => {
    const subtabParam = searchParams.get('subtab');
    if (subtabParam && VALID_SUBTABS.includes(subtabParam as ComplianceSubTab)) {
      setActiveSubTab(subtabParam as ComplianceSubTab);
    }
  }, [searchParams]);

  // Update URL when sub-tab changes
  const handleSubTabChange = (tab: ComplianceSubTab) => {
    setActiveSubTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('subtab', tab);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <ComplianceProvider clientId={clientId}>
      <ComplianceTabContent
        clientId={clientId}
        clientName={clientName}
        clientEmail={clientEmail}
        clientPhone={clientPhone}
        activeSubTab={activeSubTab}
        onSubTabChange={handleSubTabChange}
        onNavigateToDocuments={onNavigateToDocuments}
      />
    </ComplianceProvider>
  );
};

export default ComplianceTab;
